import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSession } from "./session";
import { consumeReply } from "./chatTransport";
const uuid = () => crypto.randomUUID();
export default function useConversation() {
  const { publicId } = useParams(),
    location = useLocation(),
    navigate = useNavigate();
  const { api, auth, refreshLibrary } = useSession();
  const explicitId = new URLSearchParams(location.search).get("conversation");
  const fresh = new URLSearchParams(location.search).get("new");
  const [character, setCharacter] = useState(null),
    [messages, setMessages] = useState([]),
    [images, setImages] = useState([]),
    [conversationId, setConversationId] = useState(null),
    [revision, setRevision] = useState(0),
    [loading, setLoading] = useState(true),
    [generating, setGenerating] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [pending, setPending] = useState(""),
    [retryInfo, setRetryInfo] = useState(null),
    [hasMore, setHasMore] = useState(false),
    [before, setBefore] = useState(null),
    [loadingMore, setLoadingMore] = useState(false);
  const epoch = useRef(0),
    active = useRef(null),
    operation = useRef(null),
    state = useRef({}),
    guestScene = useRef({ prompt: "", count: 0, next: 2 });
  const [sceneError, setSceneError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  state.current = {
    character,
    messages,
    images,
    conversationId,
    revision,
    generating,
    busy,
    before,
    hasMore,
    unavailable,
  };
  const sync = useCallback(
    async (id, signal, preserveRetry = false) => {
      const serial = epoch.current;
      const data = await (
        await api(`/api/conversations/${id}`, { signal })
      ).json();
      if (signal?.aborted || serial !== epoch.current) return data;
      if (data.publicId !== publicId)
        throw new Error("This conversation belongs to another character.");
      const lastKnownSeq = Math.max(
        0,
        ...state.current.messages.map((m) => m.seq || 0),
      );
      const connected =
        !lastKnownSeq || (data.messages[0]?.seq || 0) <= lastKnownSeq + 1;
      setMessages((prev) => [
        ...prev.filter(
          (m) => connected && m.seq && m.seq < (data.messages[0]?.seq || 0),
        ),
        ...data.messages,
      ]);
      setImages((prev) =>
        [
          ...prev.filter(
            (img) =>
              connected &&
              !messagesInRange(img, data.messages, state.current.messages),
          ),
          ...data.images,
        ].filter((v, i, a) => a.findIndex((x) => x.id === v.id) === i),
      );
      setRevision(data.revision);
      setBusy(data.busy);
      setUnavailable(Boolean(data.unavailable));
      if (data.unavailable) {
        setImages([]);
        setCharacter({ publicId, name: "Unavailable character", tags: [] });
      }
      if (data.retry && !data.unavailable) {
        setRetryInfo(data.retry);
        setError(
          "The last reply was interrupted. Retry to continue without sending your message twice.",
        );
      } else if (!preserveRetry) setRetryInfo(null);
      const keptEarlier =
        connected &&
        state.current.messages.some((m) => m.seq && m.seq < data.before);
      setHasMore(keptEarlier ? state.current.hasMore : data.hasMore);
      setBefore(keptEarlier ? state.current.before : data.before);
      return data;
    },
    [api, publicId],
  );
  useEffect(() => {
    if (auth.isLoading) return;
    // Updating the URL after creation must not restart the in-flight request.
    if (
      explicitId &&
      explicitId === active.current &&
      state.current.character?.publicId === publicId
    )
      return;
    const serial = ++epoch.current,
      abort = new AbortController();
    operation.current?.abort();
    operation.current = null;
    setSceneError("");
    setUnavailable(false);
    active.current = null;
    setCharacter(null);
    setMessages([]);
    setImages([]);
    setConversationId(null);
    setRevision(0);
    setLoading(true);
    setGenerating(false);
    setBusy(false);
    setError("");
    setPending("");
    setRetryInfo(null);
    setHasMore(false);
    guestScene.current = {
      prompt: "",
      count: 0,
      next: Math.floor(Math.random() * 2) + 1,
    };
    (async () => {
      try {
        let id = explicitId;
        if (auth.isAuthenticated && !id && !fresh) {
          const data = await (
            await api(`/api/conversations?publicId=${publicId}`, {
              signal: abort.signal,
            })
          ).json();
          id = data.results[0]?.id;
        }
        if (id && auth.isAuthenticated) {
          const data = await sync(id, abort.signal);
          if (serial !== epoch.current) return;
          if (data.publicId !== publicId)
            throw new Error("This conversation belongs to another character.");
          active.current = id;
          setConversationId(id);
          if (!data.unavailable) {
            const char = await (
              await api(`/api/chara/${publicId}`, { signal: abort.signal })
            ).json();
            if (serial !== epoch.current) return;
            setCharacter(char);
          }
          if (!explicitId)
            navigate(`/chat/${publicId}?conversation=${id}`, {
              replace: true,
              state: location.state,
            });
        } else {
          const char = await (
            await api(`/api/chara/${publicId}`, { signal: abort.signal })
          ).json();
          if (serial !== epoch.current) return;
          setCharacter(char);
          setMessages(
            char.greeting
              ? [
                  {
                    id: "greeting",
                    role: "assistant",
                    content: char.greeting,
                    status: "complete",
                  },
                ]
              : [],
          );
        }
      } catch (e) {
        if (e.name !== "AbortError" && serial === epoch.current)
          setError(e.message);
      } finally {
        if (serial === epoch.current) setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [
    api,
    auth.isLoading,
    auth.isAuthenticated,
    publicId,
    explicitId,
    fresh,
    navigate,
    sync,
    location.state,
  ]);
  useEffect(
    () => () => {
      ++epoch.current;
      operation.current?.abort();
    },
    [],
  );
  const refresh = async () => {
    setError("");
    setLoading(true);
    try {
      if (active.current) await sync(active.current);
      else window.location.reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  const more = async () => {
    if (!active.current || loadingMore) return;
    setLoadingMore(true);
    const serial = epoch.current;
    try {
      const d = await (
        await api(`/api/conversations/${active.current}?before=${before}`)
      ).json();
      if (serial !== epoch.current) return;
      setMessages((p) =>
        [...d.messages, ...p].filter(
          (m, i, a) => a.findIndex((x) => x.id === m.id) === i,
        ),
      );
      setImages((p) =>
        [...d.images, ...p].filter(
          (m, i, a) => a.findIndex((x) => x.id === m.id) === i,
        ),
      );
      setHasMore(d.hasMore);
      setBefore(d.before);
    } catch (e) {
      if (serial === epoch.current) setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };
  const scene = async (id, message, serial) => {
    if (state.current.unavailable || serial !== epoch.current) return;
    setSceneError("");
    try {
      if (id) {
        const d = await (
          await api(`/api/conversations/${id}/images`, {
            method: "POST",
            json: { messageId: message.id },
          })
        ).json();
        if (
          serial === epoch.current &&
          d.image &&
          state.current.messages.some(
            (m) => m.id === message.id && m.content === message.content,
          )
        )
          setImages((p) =>
            p.some((i) => i.id === d.image.id) ? p : [...p, d.image],
          );
      } else {
        const cache = guestScene.current;
        cache.count++;
        if (cache.prompt && cache.count < cache.next) return;
        const d = await (
          await api(`/api/image/${publicId}`, {
            method: "POST",
            json: cache.prompt
              ? {
                  mode: "scene",
                  basePrompt: cache.prompt,
                  recent: [...state.current.messages, message]
                    .filter((m) => m.role !== "image")
                    .slice(-6)
                    .map(({ role, content }) => ({ role, content })),
                }
              : { mode: "portrait" },
          })
        ).json();
        if (serial !== epoch.current) return;
        cache.prompt = d.prompt;
        cache.count = 0;
        cache.next = Math.floor(Math.random() * 3) + 2;
        setImages((p) => [
          ...p,
          {
            id: uuid(),
            messageId: message.id,
            url: d.image,
            createdAt: Date.now(),
          },
        ]);
      }
    } catch {
      if (serial === epoch.current)
        setSceneError(
          "The scene illustration could not be generated. Your conversation is still available.",
        );
    }
  };
  const run = async (action, content = "", messageId = null, retry = null) => {
    if (
      operation.current ||
      state.current.generating ||
      state.current.busy ||
      state.current.unavailable ||
      !state.current.character
    )
      return false;
    const serial = epoch.current,
      abort = new AbortController();
    operation.current = abort;
    setGenerating(true);
    setError("");
    setPending("");
    const info = retry || { requestId: uuid(), action, content, messageId };
    setRetryInfo(info);
    let id = active.current;
    let expected = state.current.revision;
    let result = null;
    try {
      if (auth.isAuthenticated && !id) {
        info.conversationId = info.conversationId || uuid();
        const d = await (
          await api("/api/conversations", {
            method: "POST",
            signal: abort.signal,
            json: { id: info.conversationId, publicId },
          })
        ).json();
        if (serial !== epoch.current) return false;
        id = d.id;
        expected = d.revision;
        active.current = id;
        setConversationId(id);
        setRevision(expected);
        navigate(`/chat/${publicId}?conversation=${id}`, {
          replace: true,
          state: location.state,
        });
      }
      let guestHistory;
      if (!id) {
        guestHistory =
          retry?.guestHistory || state.current.messages.map((m) => ({ ...m }));
        if (!retry) {
          if (action === "regenerate") guestHistory.pop();
          if (action === "edit") {
            const index = guestHistory.findIndex((m) => m.id === messageId);
            guestHistory = guestHistory.slice(0, index + 1);
            guestHistory[index] = { ...guestHistory[index], content };
          }
          if (action === "send")
            guestHistory.push({
              id: uuid(),
              role: "user",
              content,
              status: "complete",
            });
          info.guestHistory = guestHistory;
        }
        if (action === "send") setMessages(guestHistory);
      } else if (action === "send" && !retry)
        setMessages((prev) => [
          ...prev,
          {
            id: `local-${info.requestId}`,
            role: "user",
            content,
            status: "complete",
          },
        ]);
      const response = await api(
        id ? `/api/conversations/${id}/reply` : `/api/chat/${publicId}`,
        {
          method: "POST",
          signal: abort.signal,
          json: id
            ? {
                requestId: info.requestId,
                revision: expected,
                action: info.action,
                content: info.content,
                messageId: info.messageId,
              }
            : {
                format: "sse",
                action: info.action === "continue" ? "continue" : "send",
                messages: guestHistory.map(({ role, content }) => ({
                  role,
                  content,
                })),
              },
        },
      );
      await consumeReply(response, (event, data) => {
        if (serial !== epoch.current) return;
        if (event === "delta") setPending((p) => p + data.text);
        if (event === "done") result = data;
      });
      if (serial !== epoch.current) return false;
      if (id) {
        await sync(id, abort.signal);
        refreshLibrary();
      } else {
        setMessages([...guestHistory, result.message]);
        if (action === "regenerate" || action === "edit")
          setImages((prev) =>
            prev.filter((img) =>
              guestHistory.some((m) => m.id === img.messageId),
            ),
          );
      }
      setRetryInfo(null);
      setPending("");
      if (action === "send") void scene(id, result.message, serial);
      return true;
    } catch (e) {
      if (serial !== epoch.current || e.name === "AbortError") return false;
      setError(e.message);
      setPending("");
      if (e.status === 409) setRetryInfo(null);
      if (id) {
        try {
          await sync(id, abort.signal, true);
        } catch {
          /* Keep the failure visible until the user refreshes. */
        }
      }
      return false;
    } finally {
      if (serial === epoch.current) {
        setGenerating(false);
        operation.current = null;
      }
    }
  };
  const editAssistant = async (messageId, content) => {
    if (operation.current || state.current.busy || state.current.unavailable)
      return false;
    const serial = epoch.current,
      abort = new AbortController();
    operation.current = abort;
    setError("");
    setGenerating(true);
    try {
      if (active.current) {
        const d = await (
          await api(
            `/api/conversations/${active.current}/messages/${messageId}`,
            {
              method: "PATCH",
              signal: abort.signal,
              json: { content, revision: state.current.revision },
            },
          )
        ).json();
        if (serial !== epoch.current) return false;
        setRevision(d.revision);
        refreshLibrary();
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, content, status: "complete" } : m,
        ),
      );
      setImages((prev) => prev.filter((i) => i.messageId !== messageId));
      return true;
    } catch (e) {
      if (serial === epoch.current && e.name !== "AbortError")
        setError(e.message);
      return false;
    } finally {
      if (serial === epoch.current) {
        setGenerating(false);
        operation.current = null;
      }
    }
  };
  return {
    character,
    unavailable,
    messages,
    images,
    conversationId,
    revision,
    loading,
    generating,
    busy,
    error,
    sceneError,
    pending,
    retryInfo,
    hasMore,
    loadingMore,
    run,
    editAssistant,
    refresh,
    more,
    newChat: () =>
      navigate(`/chat/${publicId}?new=${uuid()}`, { state: location.state }),
    retry: () =>
      retryInfo &&
      run(retryInfo.action, retryInfo.content, retryInfo.messageId, retryInfo),
  };
}
function messagesInRange(image, newMessages, oldMessages) {
  const ids = new Set(newMessages.map((m) => m.id));
  return (
    ids.has(image.messageId) ||
    !oldMessages.some(
      (m) => m.id === image.messageId && m.seq < (newMessages[0]?.seq || 0),
    )
  );
}
